import React, { useState } from 'react';
import { Box, Flex, TextV2, ButtonV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useTextLineClampOverflow } from '@shared/hooks/useTextLineClampOverflow';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { SkeletonText } from '@shared/components/Skeleton';
import { ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME } from '@shared/constants/layout';

const EVENT_PHOTO_SIZE = 40;
interface HotelTileDescriptionProps
  extends Readonly<{
    description: string;
    eventPhoto?: string | null;
    enableHorizontalView?: boolean;
  }> {}

export const HotelTileDescription: React.FC<HotelTileDescriptionProps> = ({ description, eventPhoto, enableHorizontalView = false }) => {
  const { tq } = useTranslation('hotelTile');
  const { textRef, isOverflowing } = useTextLineClampOverflow(2);
  const [isShowingMore, setShowMore] = useState(false);

  const handleToggle = useEventCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setShowMore(prev => !prev);
  });

  return (
    <Flex
      gap={3}
      {...(enableHorizontalView && { marginY: 3 })}
    >
      {eventPhoto && enableHorizontalView && (
        <Box
          width={pxToRem(EVENT_PHOTO_SIZE)}
          backgroundImage={`url(${eventPhoto})`}
          height={pxToRem(EVENT_PHOTO_SIZE)}
          borderRadius="full"
          overflow="hidden"
          backgroundSize="cover"
          backgroundPosition="center"
        />
      )}
      <Flex
        width="100%"
        alignItems="flex-end"
      >
        <TextEllipsisWrapper
          ref={textRef}
          lineClamp={isShowingMore ? 'none' : 2}
          lineHeight={1.5}
          className={ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME}
        >
          <TextV2
            typographyVariant="body1"
            fontFamily="Inter UI"
            fontWeight={400}
            color="mono10"
            title={description}
            lineHeight={1.5}
          >
            {description}
          </TextV2>
        </TextEllipsisWrapper>
        {isOverflowing && (
          <ButtonV2
            variant="ghost"
            intent="neutral"
            fontWeight={400}
            color="mono10"
            onClick={handleToggle}
            fontSize={pxToRem(16)}
            fontFamily="Inter UI"
            lineHeight={1.5}
            textDecoration="underline"
            padding={0}
            height="auto"
            minHeight="auto"
            _hover={{ textDecoration: 'underline' }}
          >
            {isShowingMore ? tq`Less` : tq`More`}
          </ButtonV2>
        )}
      </Flex>
    </Flex>
  );
};

// Skeleton component for HotelTileDescription
export const HotelTileDescriptionSkeleton: React.FC = () => {
  return (
    <Box>
      <SkeletonText
        rows={1}
        width="100%"
        paddingBottom={pxToRem(2)}
        skeletonHeight={pxToRem(16)}
      />
      <SkeletonText
        rows={1}
        width="70%"
        skeletonHeight={pxToRem(16)}
      />
    </Box>
  );
};
